import {
  BufferAttribute,
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { ToolState } from '../core/GameState';
import { RescueTool } from './RescueTool';

export type AutoSalvagePhase = 'deploying' | 'scanning' | 'lifting' | 'complete';

export interface AutoSalvageTelemetry {
  phase: AutoSalvagePhase;
  progress: number;
  lift: number;
  complete: boolean;
}

const easeOutCubic = (value: number): number => 1 - Math.pow(1 - value, 3);

export class AutoSalvageFleet extends RescueTool {
  phase: AutoSalvagePhase = 'deploying';
  private phaseElapsed = 0;
  private readonly start = new Vector3();
  private readonly hover = new Vector3();
  private readonly saucers = new Group();
  private readonly units: Group[] = [];
  private readonly beams: Mesh[] = [];
  private readonly cableGeometry = new BufferGeometry();
  private readonly cables: LineSegments;
  private readonly offsets = [
    new Vector3(-2.8, 0, 1.5),
    new Vector3(2.8, 0.15, 1.5),
    new Vector3(0, 0.35, -2.4),
  ];

  constructor() {
    super('autoSalvage');
    this.group.visible = false;
    this.group.name = 'AutoSalvageUfoFleet';
    this.offsets.forEach((offset, index) => {
      const saucer = this.createSaucer(index);
      saucer.position.copy(offset);
      this.units.push(saucer);
      this.saucers.add(saucer);

      const beam = new Mesh(
        new ConeGeometry(1.65, 6.2, 18, 1, true),
        new MeshBasicMaterial({
          color: index === 1 ? 0xffd966 : 0x8ff8e9,
          transparent: true,
          opacity: 0.16,
          depthWrite: false,
          side: 2,
        }),
      );
      beam.position.copy(offset).add(new Vector3(0, -3.2, 0));
      beam.rotation.z = Math.PI;
      beam.visible = false;
      this.beams.push(beam);
      this.saucers.add(beam);
    });

    this.cableGeometry.setAttribute('position', new BufferAttribute(new Float32Array(18), 3));
    this.cables = new LineSegments(
      this.cableGeometry,
      new LineBasicMaterial({ color: 0xfff4ae, transparent: true, opacity: 0.92 }),
    );
    this.cables.visible = false;
    this.group.add(this.saucers, this.cables);
  }

  private createSaucer(index: number): Group {
    const saucer = new Group();
    const shell = new MeshStandardMaterial({
      color: index === 1 ? 0x58d8ca : 0x7fe8dc,
      roughness: 0.3,
      metalness: 0.38,
      flatShading: true,
    });
    const dark = new MeshStandardMaterial({
      color: 0x314c62,
      roughness: 0.42,
      metalness: 0.44,
      flatShading: true,
    });
    const hull = new Mesh(new CylinderGeometry(1.75, 1.18, 0.58, 16), shell);
    hull.castShadow = true;
    const rim = new Mesh(new TorusGeometry(1.38, 0.18, 8, 20), dark);
    rim.rotation.x = Math.PI / 2;
    const dome = new Mesh(
      new SphereGeometry(0.88, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2),
      new MeshStandardMaterial({
        color: 0xcafcff,
        roughness: 0.12,
        metalness: 0.08,
        transparent: true,
        opacity: 0.78,
      }),
    );
    dome.position.y = 0.25;
    const belly = new Mesh(new CylinderGeometry(0.68, 0.92, 0.42, 12), dark);
    belly.position.y = -0.42;
    saucer.add(hull, rim, dome, belly);
    for (let lightIndex = 0; lightIndex < 6; lightIndex += 1) {
      const light = new Mesh(
        new SphereGeometry(0.11, 7, 5),
        new MeshBasicMaterial({ color: lightIndex % 2 ? 0xffd966 : 0xff6b4a }),
      );
      const angle = (lightIndex / 6) * Math.PI * 2;
      light.position.set(Math.cos(angle) * 1.42, -0.15, Math.sin(angle) * 1.42);
      light.name = 'salvageLight';
      saucer.add(light);
    }
    return saucer;
  }

  deploy(target: Vector3): void {
    this.phase = 'deploying';
    this.phaseElapsed = 0;
    this.start.copy(target).add(new Vector3(24, 17, 17));
    this.hover.copy(target).add(new Vector3(0, 8.3, 0));
    this.group.position.copy(this.start);
    this.group.scale.setScalar(0.35);
    this.group.visible = true;
    this.state = ToolState.SELECTED;
  }

  updateAutomatic(delta: number, target: Vector3): AutoSalvageTelemetry {
    this.phaseElapsed += delta;
    this.hover.set(target.x, target.y + 8.3, target.z);
    let progress = 0;
    let lift = 0;

    if (this.phase === 'deploying') {
      progress = Math.min(1, this.phaseElapsed / 1.05);
      const eased = easeOutCubic(progress);
      this.group.position.lerpVectors(this.start, this.hover, eased);
      this.group.scale.setScalar(0.35 + eased * 0.65);
      if (progress >= 1) this.nextPhase('scanning');
    } else if (this.phase === 'scanning') {
      progress = Math.min(1, this.phaseElapsed / 0.9);
      this.group.position.lerp(this.hover, Math.min(1, delta * 5));
      this.beams.forEach((beam) => {
        beam.visible = true;
        (beam.material as MeshBasicMaterial).opacity = 0.11 + Math.sin(this.phaseElapsed * 8) * 0.05;
      });
      if (progress >= 1) this.nextPhase('lifting');
    } else if (this.phase === 'lifting') {
      progress = Math.min(1, this.phaseElapsed / 1.25);
      lift = easeOutCubic(progress);
      this.group.position.lerp(this.hover, Math.min(1, delta * 6));
      this.beams.forEach((beam) => {
        beam.visible = false;
      });
      this.cables.visible = true;
      this.updateCables(target, lift);
      if (progress >= 1) this.nextPhase('complete');
    } else {
      progress = 1;
      lift = 1;
      this.cables.visible = true;
      this.updateCables(target, lift);
    }

    this.saucers.rotation.y += delta * (this.phase === 'scanning' ? 1.7 : 0.55);
    this.units.forEach((unit, index) => {
      unit.position.y = this.offsets[index].y + Math.sin(this.phaseElapsed * 4 + index) * 0.12;
    });

    const phaseOffset = this.phase === 'deploying' ? 0 : this.phase === 'scanning' ? 1 : this.phase === 'lifting' ? 2 : 3;
    return {
      phase: this.phase,
      progress: Math.min(1, (phaseOffset + progress) / 3),
      lift,
      complete: this.phase === 'complete',
    };
  }

  private nextPhase(phase: AutoSalvagePhase): void {
    this.phase = phase;
    this.phaseElapsed = 0;
  }

  private updateCables(target: Vector3, lift: number): void {
    const positions = this.cableGeometry.getAttribute('position') as BufferAttribute;
    const localTarget = target.clone().sub(this.group.position);
    localTarget.y += 1.4 + lift * 1.8;
    this.offsets.forEach((offset, index) => {
      positions.setXYZ(index * 2, offset.x, offset.y - 0.45, offset.z);
      positions.setXYZ(
        index * 2 + 1,
        localTarget.x + (index - 1) * 0.38,
        localTarget.y,
        localTarget.z + (index === 2 ? -0.32 : 0.22),
      );
    });
    positions.needsUpdate = true;
  }

  override dispose(): void {
    this.cableGeometry.dispose();
    (this.cables.material as LineBasicMaterial).dispose();
    super.dispose();
  }
}
